import React, { useState, useEffect } from 'react';
import { DataResponse } from '@playball/common/dto/Common';
import { useAuth } from '../context/AuthContext';
import { LeagueParticipantRankResponse, LeagueParticipantRankDto } from '@playball/common/dto/League';
import { AccountDto } from '@playball/common/dto/Account';

export const NewPodium: React.FC<{ topRanks: LeagueParticipantRankDto[], user: AccountDto | null, simpleView?: boolean }> = ({ topRanks, user, simpleView = false }) => {
    if (!topRanks || topRanks.length === 0) {
        return <p className="text-center h-100 d-flex align-items-center justify-content-center mb-0" style={{ minHeight: simpleView ? '160px' : '240px' }}>랭킹 기록이 존재하지 않습니다</p>;
    }

    // simpleView일 경우 상위 3명만 표시
    const ranksToShow = simpleView ? topRanks.slice(0, 3) : topRanks;

    return (
        <div className="row text-center align-items-end h-100" style={{ minHeight: simpleView ? '160px' : '240px' }}>
            {ranksToShow.map((rank, index) => {
                // 순위에 따라 높이 조절 (simpleView에서는 높이를 낮춤)
                const heightClass = simpleView
                    ? ['', '10rem', '8rem', '6rem'][rank.rank]
                    : ['', '15rem', '12rem', '10rem'][rank.rank];

                return (
                    <div className="col-4" key={index}>
                        <div className={`p-2 border rounded-3 shadow-sm d-flex flex-column justify-content-center ${rank.nickname === user?.nickname ? 'bg-info' : 'bg-light'}`} style={{ height: heightClass }}>
                            <h3 className={`h6 fw-bold`}>{['', '🥇', '🥈', '🥉'][rank.rank]} {rank.rank}{['', 'st', 'nd', 'rd'][rank.rank]}</h3>
                            <p className={`mb-0 ${simpleView ? 'small' : 'lead'}`}>{rank.nickname}</p>
                            {!simpleView && <p className="text-muted">{rank.score} pts</p>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const UserRankingPage: React.FC = () => {
    const [rankingData, setRankingData] = useState<LeagueParticipantRankResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const { fetchApi, user } = useAuth();

    useEffect(() => {
        const fetchRankingData = async () => {
            setIsLoading(true);
            try {
                const data = await fetchApi(`/api/league/rank`) as DataResponse<LeagueParticipantRankResponse>;
                setRankingData(data.data);

            } catch (err: any) {
                setError("선수 정보를 불러오는 데 실패했습니다.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchRankingData();
    }, [fetchApi]);

    const renderRankTable = (title: string, ranks: LeagueParticipantRankDto[], myRank: LeagueParticipantRankDto | null) => {
        return (
            <div className="card shadow-sm mb-4">
                <div className="card-header bg-primary text-white">
                    <h2 className="h5 mb-0">{title}</h2>
                </div>
                <div className="card-body">
                    {
                        ranks.length === 0
                        ? <p className="text-center">랭킹 기록이 존재하지 않습니다</p>
                        : <div className="table-responsive">
                            <table className="table table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th scope="col">Rank</th>
                                        <th scope="col">Nickname</th>
                                        <th scope="col">Score</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        ranks.map((rank, index) => (
                                            <tr key={index} className={myRank && rank.nickname === myRank.nickname ? 'table-info' : ''}>
                                                <td><h5><span className="badge bg-secondary">{rank.rank}</span></h5></td>
                                                <td>{rank.nickname}</td>
                                                <td>{rank.score}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                                {
                                    user && <>
                                        <thead className="table-light">
                                            <tr>
                                                <th scope="col">My Rank</th>
                                                <th scope="col">Nickname</th>
                                                <th scope="col">Score</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className='table-info'>
                                                <td><h5><span className="badge bg-secondary">{myRank?.rank || '-'}</span></h5></td>
                                                <td>{myRank?.nickname || user.nickname}</td>
                                                <td>{myRank?.score || '-'}</td>
                                            </tr>
                                        </tbody>
                                    </>
                                }
                            </table>
                        </div>
                    }
                </div>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger" role="alert">
                    <strong>Error:</strong> {error}
                </div>
            </div>
        );
    }

    if (!rankingData) {
        return (
            <div className="container mt-4">
                <div className="alert alert-info" role="alert">
                    No ranking data available.
                </div>
            </div>
        );
    }

    return (
        <>
            <section className="guide-hero-section py-5">
                <div className="container text-center">
                    <h1 className="display-5 fw-bold">유저 랭킹</h1>
                </div>
            </section>
            <div className="container mt-5">
                <div className="row">
                    <div className="col-lg-6">
                        <div className="card shadow-sm mb-5">
                            <div className="card-header bg-success text-white">
                                <h2 className="h4 mb-0">일일 명예의 전당</h2>
                            </div>
                            <div className="card-body">
                                <NewPodium topRanks={rankingData.dailyRankList.slice(0, 3)} user={user} />
                            </div>
                        </div>
                        {renderRankTable("일간 랭킹", rankingData.dailyRankList, rankingData.myDailyRank)}
                    </div>
                    <div className="col-lg-6">
                        <div className="card shadow-sm mb-5">
                            <div className="card-header bg-success text-white">
                                <h2 className="h4 mb-0">주간 명예의 전당</h2>
                            </div>
                            <div className="card-body">
                                <NewPodium topRanks={rankingData.weeklyRankList.slice(0, 3)} user={user} />
                            </div>
                        </div>
                        {renderRankTable("주간 랭킹", rankingData.weeklyRankList, rankingData.myWeeklyRank)}
                    </div>
                </div>
                <div className="text-center text-muted mt-4">
                    <p>Last updated: {rankingData.refreshDate}</p>
                </div>
            </div>
        </>
    );
};

export default UserRankingPage;